// AML/PEP flagged policies may only be approved or rejected once the reviewer
// has recorded a reason AND attached a supporting document. The rule is shared
// by the decision dialog (PolicyDetailsFlp) and the policy header, which hides
// its plain Approve/Reject while a flag is still undecided.

export const FLAG_PREFIXES = ['aml', 'pep']

export const SUPPORTING_DOCUMENT_LABELS = {
  aml: 'AML Supporting Document',
  pep: 'PEP Supporting Document'
}

// The screenings a maker-check stage may be reviewing. IPRS sits outside
// FLAG_PREFIXES because it carries no supporting-document requirement.
export const SCREENING_PREFIXES = ['aml', 'pep', 'iprs']

export const SCREENING_LABELS = {
  aml: 'AML',
  pep: 'PEP',
  iprs: 'IPRS'
}

// A stage is named by a partner-configured label over a type (AmlApproval,
// PepApproval, IprsApproval, or a plain AgentApproval labelled "AML Check"), so
// both are matched — the same rule the dashboards use in
// components/RecentPolicies/Components/makerCheckStage.
function matchPrefix(check, prefixes) {
  if (!check) {
    return null
  }
  const name = `${String(check.label || '')} ${String(
    check.type || ''
  )}`.toLowerCase()
  return (
    prefixes.find(function(prefix) {
      return name.indexOf(prefix) !== -1
    }) || null
  )
}

// Which flag a maker-check stage decides, or null for a stage that is not an
// AML/PEP screening stage.
export function screeningFlagPrefix(check) {
  return matchPrefix(check, FLAG_PREFIXES)
}

// Which screening a maker-check stage reviews. Wider than the AML/PEP pair
// above because IPRS is screened and reasoned about the same way, but carries
// no supporting-document requirement.
export function screeningPrefix(check) {
  return matchPrefix(check, SCREENING_PREFIXES)
}

// The reasons the onboarding flow recorded when it ran the screening, so the
// reviewer decides the stage against what was actually found rather than
// against the stage name alone. IPRS is screened per person, so the policy
// holder's reason is joined by the next of kin's and by any dependant carrying
// one. Only the reason text is surfaced: `<name>_flagged` does not have a
// consistent polarity across the screenings (a true iprs_flagged means the
// registry matched, a true pep_flagged means a hit to review), so rendering it
// as a verdict would mislead.
export function screeningReasons(check, policy) {
  const prefix = screeningPrefix(check)
  if (!prefix) {
    return []
  }
  const details = (policy && policy.additional_details) || {}
  const entries = [
    { subject: 'Policy holder', reason: details[`${prefix}_reason`] }
  ]
  if (prefix === 'iprs') {
    entries.push({
      subject: 'Next of kin',
      reason: details.next_of_kin_iprs_reason
    })
    dependantsOf(policy).forEach(function(dependant) {
      entries.push({
        subject: dependantName(dependant),
        reason: (dependant.additional_details || {}).iprs_reason
      })
    })
  }
  return entries.filter(function(entry) {
    return !!String(entry.reason || '').trim()
  })
}

export function screeningLabel(check) {
  const prefix = screeningPrefix(check)
  return prefix ? SCREENING_LABELS[prefix] : null
}

function dependantsOf(policy) {
  const entries =
    (policy && policy.quotation && policy.quotation.policy_schedule_entries) ||
    []
  return entries
    .map(function(entry) {
      return entry && entry.dependant
    })
    .filter(Boolean)
}

function dependantName(dependant) {
  const name = [
    dependant.full_name,
    dependant.first_name,
    dependant.middle_name,
    dependant.last_name
  ]
    .filter(Boolean)
    .join(' ')
  const relationship = dependant.relationship
  if (name && relationship) {
    return `${name} (${relationship})`
  }
  return name || relationship || 'Member'
}

// True while the policy carries a flag the reviewer has not yet acted on.
export function hasUndecidedFlag(policy) {
  const details = (policy && policy.additional_details) || {}
  return FLAG_PREFIXES.some(function(prefix) {
    return !!details[`${prefix}_flagged`] && !details[`${prefix}_action`]
  })
}

// The document may have been attached on an earlier visit, so a matching
// document already on the policy satisfies the requirement.
export function findSupportingDocument(policy, flagPrefix) {
  const label = SUPPORTING_DOCUMENT_LABELS[flagPrefix]
  const documents = (policy && policy.policy_onboarding_documents) || []
  return documents.find(function(doc) {
    return doc && (doc.description === label || doc.filename === label)
  })
}

// Phrases naming what is still missing, for the message shown to the reviewer.
// Empty means the decision may go ahead.
export function missingDecisionRequirements(reason, hasSupportingDocument) {
  return [
    !(reason || '').trim() ? 'a reason for the action is given' : null,
    !hasSupportingDocument ? 'a supporting document is attached' : null
  ].filter(Boolean)
}
